import { ValidationError } from "sequelize"
import {
    Lifecycle,
    LifecycleCreate,
    LifecycleSave,
} from "server/models"
import { ASC, NONE, VALUE, UNLIMITED } from "const"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import {
    LangType,
    PackageSettings,
    UserData,
    LifecycleList,
    LifecycleValidation,
    LifecycleImportFilters,
    LoadNestedLifecyclePhases,
    ImportLifecyclePhases,
} from "client/store"
import { OrderType } from "client/utils"


export const LoadAllLifecycles = (accountId: number, projectId: number) => (
    GET(`/lifecycle/accountId/${accountId}/projectId/${projectId}`)
    .then((lifecycles: Lifecycle[]) => {
        const lifecycleList: LifecycleList = {}
        lifecycles.forEach((lifecycle) => {
            lifecycleList[lifecycle.id] = lifecycle
        })

        const lifecyclesSorted = lifecycles.sort((a, b) => sort(a, b, "order", ASC))
        const lifecycleIds: number[] = []
        lifecyclesSorted.forEach((lifecycle) => {
            lifecycleIds.push(lifecycle.id)
        })

        return { lifecycleList, lifecycleIds }
    })
)

export const LoadLifecycleById = (
    lifecycleId: number,
    accountId: number,
    projectId: number,
) => (
    GET(`/lifecycle/id/${lifecycleId}/accountId/${accountId}/projectId/${projectId}`)
    .then((lifecycle: Lifecycle) => lifecycle)
)

const Save = (
    data: LifecycleSave,
    lang?: LangType,
) => (
    POST(
        "/lifecycle/save",
        JSON.stringify(data),
    ).then((res) => {
        const newLifecycle = res as Lifecycle
        const error = res as ValidationError

        return {
            newLifecycleId: newLifecycle.id || null,
            validation: lang ? <LifecycleValidation>validate(error, lang) : null,
        }
    })
)

export const SaveLifecycle = (
    id: number,
    lifecycle: LifecycleCreate,
    maxLifecycleNumber: number,
    lang: LangType,
) => (
    LoadAllLifecycles(lifecycle.accountId, lifecycle.projectId).then((res1) => {
        const lifecyclesNumber = Object.keys(res1.lifecycleList).length
        if (!id && maxLifecycleNumber !== null && lifecyclesNumber >= maxLifecycleNumber) {
            return {
                newLifecycleId: null,
                validation: <LifecycleValidation>{
                    name: lang.maxLifecyclesReached.replace("{0}", maxLifecycleNumber.toString()),
                },
            }
        }

        const data: LifecycleSave = {
            id,
            lifecycle,
        }
        return Save(data, lang)
    })
)

export const DragnDropLifecycle = (
    lifecycleIds: readonly number[],
    lifecycleList: LifecycleList,
) => {
    const data: Lifecycle[] = []
    lifecycleIds.forEach((id, index) => {
        data.push({
            ...lifecycleList[id],
            order: index,
        })
    })

    return POST(
        "/lifecycle/updateMany",
        JSON.stringify(data),
    ).then((success: boolean) => success)
}

export const DeleteLifecycle = (lifecycleId: number) => (
    DELETE(`/lifecycle/delete/${lifecycleId}`)
    .then((removedRows: number) => removedRows)
)

export const SortLifecycles = (
    lifecycles: Lifecycle[],
    sortColumn: keyof Lifecycle,
    sortOrder: OrderType,
    filters: LifecycleImportFilters,
) => {
    const lifecyclesFiltered = lifecycles.filter((lifecycle) => {
        let showRow = true
        if (filters.search) showRow = search(filters.search, lifecycle)
        return showRow
    })
    lifecyclesFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))

    const lifecycleIds: number[] = []
    lifecyclesFiltered.forEach((lifecycle) => lifecycleIds.push(lifecycle.id))

    return lifecycleIds
}

export const ImportLifecycle = (
    accountId: number,
    projectId: number,
    lifecycle: Lifecycle,
) => {
    const newLifecycleData: LifecycleCreate = {
        originalLifecycleId: lifecycle.id,
        accountId,
        projectId,
        name: lifecycle.name,
        description: lifecycle.description,
        type: lifecycle.type,
        ownerAccount: lifecycle.ownerAccount,
        creator: lifecycle.creator,
        responsible: null,
        order: 0,
        default: false,
    }

    return Save({
        id: null,
        lifecycle: newLifecycleData,
    }).then(({ newLifecycleId }) => {
        if (!newLifecycleId) return null
        const newLifecycle: Lifecycle = {
            id: newLifecycleId,
            ...newLifecycleData,
        }

        return LoadNestedLifecyclePhases(lifecycle.id)
        .then((nestedPhase) => (
            ImportLifecyclePhases(nestedPhase, newLifecycleId)
        )).then(() => newLifecycle)
    })
}

export const CopyDefaultLifecycles = (
    accountId: number,
    projectId: number,
    defaultLifecyclesAssigned: boolean,
) => (
    GET(`/lifecycle/default/accountId/${defaultLifecyclesAssigned ? accountId : 1}`)
    .then((lifecycles: Lifecycle[]) => {
        const promiseArray: Promise<any>[] = []
        lifecycles.forEach((lifecycle) => {
            promiseArray.push(
                ImportLifecycle(accountId, projectId, lifecycle),
            )
        })

        return Promise.all(promiseArray)
    })
)

export const GetMaxLifecycles = (
    packageSettings: PackageSettings,
    userData: UserData,
    lifecyclesNumber?: number,
    lang?: LangType,
) => {
    const { lifecycles, lifecyclesValue } = packageSettings

    let maxLifecycles = 0
    if (lifecycles === NONE) maxLifecycles = 0
    if (lifecycles === VALUE && lifecyclesValue) maxLifecycles = lifecyclesValue
    if (lifecycles === UNLIMITED) maxLifecycles = null
    if (userData.accountId === 1) maxLifecycles = null

    // feedbacks
    let feedback: string = ""
    if (lang) {
        if (maxLifecycles === null) {
            feedback = lang.maxLifecyclesUnlimited
        } else {
            if (lifecyclesNumber < maxLifecycles) feedback = lang.maxLifecycles
            if (lifecyclesNumber === maxLifecycles) feedback = lang.maxLifecyclesReached
            if (lifecyclesNumber > maxLifecycles) feedback = lang.maxLifecyclesExceeded
            feedback = feedback.replace("{0}", maxLifecycles.toString())
        }
    }

    return {
        maxLifecycles,
        feedback,
    }
}

export const IsOwnerLifecycle = (
    projectId: number,
    lifecycle: Lifecycle,
    userData: UserData,
) => (
    !projectId
    && lifecycle
    && lifecycle.ownerAccount === userData.accountId
    && lifecycle.creator === userData.userId
)

export const IsResponsibleLifecycle = (
    lifecycle: Lifecycle,
    userData: UserData,
) => (
    lifecycle
    && lifecycle.responsible === userData.userId
)
